import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile"
import { ClientOnly } from "@tanstack/react-router"
import { useContext, useEffect, useRef, useState } from "react"

import { useTheme } from "@repo/ui/components/theming/theme-provider"
import { Skeleton } from "@repo/ui/components/ui/skeleton"
import { cn } from "@repo/ui/lib/utils"

import { CaptchaContext } from "./captcha-provider"
import { readCspNonce } from "./csp-nonce"

export type TurnstileWidgetProps = {
  className?: string
}

export function TurnstileWidget({ className }: TurnstileWidgetProps) {
  const { captchaEnabled, siteKey, setToken, setInstance } =
    useContext(CaptchaContext)
  const { resolvedTheme } = useTheme()
  const turnstileRef = useRef<TurnstileInstance>(null)
  const firstLoad = useRef(true)
  const [isVisible, setIsVisible] = useState(true)

  // Re-render widget when theme changes
  useEffect(() => {
    turnstileRef.current?.reset()
    firstLoad.current = false
  }, [resolvedTheme])

  // Don't render if captcha is disabled or no site key
  if (!captchaEnabled || !siteKey) return null

  return (
    <ClientOnly
      fallback={
        <Skeleton
          className={cn("flex h-10 w-full justify-center", className)}
        />
      }
    >
      <Turnstile
        className={cn(
          "w-full flex-col items-stretch justify-stretch overflow-hidden",
          className,
        )}
        style={{
          display: isVisible || firstLoad.current ? "flex" : "none",
          position: firstLoad.current ? "absolute" : "relative",
          height: firstLoad.current ? "1px" : "auto",
          opacity: firstLoad.current ? 0 : 1,
        }}
        ref={(instance) => {
          if (instance) {
            turnstileRef.current = instance
            setInstance(instance)
          }
        }}
        siteKey={siteKey}
        onSuccess={(token) => {
          setToken(token)
          setIsVisible(false)
        }}
        onBeforeInteractive={() => setIsVisible(true)}
        onAfterInteractive={() => setIsVisible(false)}
        options={{
          size: "flexible",
          theme: resolvedTheme === "dark" ? "dark" : "light",
          appearance: "always",
        }}
        scriptOptions={{ nonce: readCspNonce() }}
      />
    </ClientOnly>
  )
}
